// Generic validation schema using Yup - Reusable across all modules
import * as yup from 'yup';

// Helper function to create validation schema from any module's field config
const createValidationSchema = (fields) => {
  const shape = {};
  
  fields.forEach(field => {
    if (!field.validation || field.type === 'hidden') return;
    
    let fieldSchema;
    
    // Base schema based on field type
    switch (field.type) {
      case 'email':
        fieldSchema = yup.string().email('Please enter a valid email address');
        break;
      case 'url':
        fieldSchema = yup.string().url('Please enter a valid URL');
        break;
      case 'number':
        fieldSchema = yup.number().typeError('Please enter a valid number');
        break;
      case 'date':
        // Date inputs clear to "" — treat empty as null so optional dates can be wiped
        // (e.g. reopening a closed complaint and clearing Closed/Resolved Date).
        fieldSchema = yup
          .date()
          .nullable()
          .transform((value, originalValue) => {
            if (
              originalValue === '' ||
              originalValue === null ||
              originalValue === undefined
            ) {
              return null;
            }
            if (typeof originalValue === 'string' && originalValue.trim() === '') {
              return null;
            }
            return value;
          })
          .typeError(
            field.validation?.message || 'Please enter a valid date',
          );
        break;
      case 'boolean':
      case 'switch':
        fieldSchema = yup.boolean();
        break;
      case 'multiSelect':
        // MultiSelect fields are arrays (typically of numbers for IDs)
        fieldSchema = yup.array().of(yup.mixed());
        break;
      default:
        fieldSchema = yup.string();
    }
    
    // Apply validation rules from config
    const validation = field.validation;
    
    // Required validation
    if (validation.required) {
      if (field.type === 'boolean' || field.type === 'switch') {
        fieldSchema = fieldSchema.required(`${field.label} is required`);
      } else {
        fieldSchema = fieldSchema.required(`${field.label} is required`);
      }
    } else {
      // Allow nullable/optional for non-required fields
      fieldSchema = fieldSchema.nullable().optional();
    }
    
    // String-specific validations
    if (fieldSchema instanceof yup.StringSchema) {
      // Min/Max length
      if (validation.minLength) {
        fieldSchema = fieldSchema.min(validation.minLength, 
          `${field.label} must be at least ${validation.minLength} characters`);
      }
      if (validation.maxLength) {
        fieldSchema = fieldSchema.max(validation.maxLength, 
          `${field.label} must not exceed ${validation.maxLength} characters`);
      }
      
      // Pattern validation
      if (validation.pattern) {
        fieldSchema = fieldSchema.matches(validation.pattern, 
          validation.message || `${field.label} format is invalid`);
      }
    }
    
    // Number-specific validations
    if (fieldSchema instanceof yup.NumberSchema) {
      if (validation.min !== undefined) {
        fieldSchema = fieldSchema.min(validation.min, 
          `${field.label} must be at least ${validation.min}`);
      }
      if (validation.max !== undefined) {
        fieldSchema = fieldSchema.max(validation.max, 
          `${field.label} must not exceed ${validation.max}`);
      }
      
      // Integer validation
      if (validation.integer) {
        fieldSchema = fieldSchema.integer(
          validation.message || `${field.label} must be a whole number`
        );
      }
    }
    
    // Date-specific validations
    if (fieldSchema instanceof yup.DateSchema) {
      if (validation.minDate) {
        fieldSchema = fieldSchema.min(validation.minDate, 
          `${field.label} must be after ${validation.minDate}`);
      }
      if (validation.maxDate) {
        fieldSchema = fieldSchema.max(validation.maxDate, 
          `${field.label} must be before ${validation.maxDate}`);
      }
    }
    
    // Custom validation function
    if (validation.custom && typeof validation.custom === 'function') {
      fieldSchema = fieldSchema.test('custom', validation.customMessage || `${field.label} is invalid`, validation.custom);
    }
    
    shape[field.name] = fieldSchema;
  });
  
  return yup.object().shape(shape);
};

/**
 * Generic factory function to create validation functions for any module
 * @param {Array} fields - Field configuration array with validation rules
 * @returns {Object} - Validation functions (schema, validateData, validateField)
 */
export const createValidation = (fields) => {
  const schema = createValidationSchema(fields);
  
  const validateData = async (data) => {
    try {
      await schema.validate(data, { abortEarly: false });
      return {
        isValid: true,
        errors: {}
      };
    } catch (err) {
      const errors = {};
      
      if (err.inner) {
        err.inner.forEach(error => {
          errors[error.path] = error.message;
        });
      }
      
      return {
        isValid: false,
        errors
      };
    }
  };

  const validateField = async (fieldName, value, allData = {}) => {
    try {
      const fieldSchema = schema.fields[fieldName];
      if (!fieldSchema) return null;
      
      await fieldSchema.validate(value);
      return null; // No error
    } catch (err) {
      return err.message;
    }
  };

  return {
    schema,
    validateData,
    validateField
  };
};

// Default export
export default createValidation;

/**
 * Usage Examples:
 * 
 * // For any module (documents, users, etc.)
 * import { createValidation } from '../utils/validation';
 * 
 * const validation = createValidation(moduleConfig.fields);
 * const result = await validation.validateData(formData);
 * const fieldError = await validation.validateField('title', value);
 */
